import { inject, Injectable } from '@angular/core';
import {
  ColDef,
  CellClickedEvent,
  CheckboxSelectionCallbackParams,
  EditableCallbackParams,
  ICellRendererParams,
  ValueGetterParams,
  CellClassParams,
  GridApi,
} from 'ag-grid-community';
import { Observable } from 'rxjs';

import { DateFormatService } from '../date-format.service';
import { FormatService } from '../format.service';
import { LanguageService } from '../language.service';
import { MenuService } from '../menu.service';
import { UtilityService } from '../utility.service';
import { AggFunc, BooleanStrings } from '../../constants';
import { doNothing } from '../../helpers';
import { GridFieldOptions, IdDescription } from '../../models';
import { BadgeRendererComponent } from '../../components/grids/cell-renderers/badge-renderer/badge-renderer.component';
import { ButtonRendererComponent } from '../../components/grids/cell-renderers/button-renderer/button-renderer.component';
import { CheckboxRendererComponent } from '../../components/grids/cell-renderers/checkbox-renderer/checkbox-renderer.component';
import { DateFilterComponent } from '../../components/grids/filters/date-filter/date-filter.component';
import { DateRendererComponent } from '../../components/grids/cell-renderers/date-renderer/date-renderer.component';
import { ImageRendererComponent } from '../../components/grids/cell-renderers/image-renderer/image-renderer.component';
import { SelectRendererComponent } from '../../components/grids/cell-renderers/select-renderer/select-renderer.component';

@Injectable({
  providedIn: 'root',
})
export class GridService {
  protected readonly formatService = inject(FormatService);
  protected readonly languageService = inject(LanguageService);
  protected readonly menuService = inject(MenuService);
  protected readonly dateFormatService = inject(DateFormatService);
  protected readonly utilityService = inject(UtilityService);

  components: any = {
    agDateInput: DateFilterComponent,
    badgeRenderer: BadgeRendererComponent,
    buttonRenderer: ButtonRendererComponent,
    checkboxRenderer: CheckboxRendererComponent,
    dateRenderer: DateRendererComponent,
    imageRenderer: ImageRendererComponent,
    selectRenderer: SelectRendererComponent,
  };

  // A value shown as a Bootstrap badge, e.g. a status: badgeClass gives the colour classes for a
  // value ('text-bg-success'), label the text a person reads (the raw value when omitted). The
  // column filter matches the label, as checkbox() matches its translated Yes/No.
  badge(
    field: string,
    headerName: string,
    badgeClass: (value: string) => string,
    label?: (value: string) => string,
    options?: GridFieldOptions,
  ): ColDef {
    return {
      ...this.text(field, headerName, options),
      cellClass: 'd-flex align-items-center',
      cellRenderer: 'badgeRenderer',
      cellRendererParams: {
        badgeClass,
        label,
      },
      ...(label ? { filterValueGetter: (params: ValueGetterParams) => label(params.data?.[field]) } : {}),
      ...options,
    };
  }

  button<T>(
    onClick: (model: T) => void,
    header: string,
    field: string,
    className: string,
    iconClass: string | ((data: T) => string),
    show?: (data: T) => boolean,
    options?: GridFieldOptions,
    buttonText?: string | ((data: T) => string),
    disabled?: (data: T) => boolean,
  ): ColDef {
    return {
      headerName: header,
      field,
      cellClass: 'd-flex align-items-center',
      cellRenderer: 'buttonRenderer',
      cellRendererParams: {
        className,
        iconClass,
        show,
        onClick,
        buttonText,
        disabled,
      },
      width: 40,
      filter: false,
      sortable: false,
      suppressMenu: true,
      onCellClicked: doNothing,
      enableRowGroup: false,
      ...options,
    };
  }

  commentButton(onClick: (model: any) => void): ColDef {
    return this.button(onClick, '', 'comment', 'comment', (model: any) =>
      !model?.commentsExist ? 'far fa-comment' : 'fa fa-comment',
    );
  }

  calculation(field: string, headerName: string, calculation: (model: any) => any, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      calculation,
      valueGetter: (params: ValueGetterParams) => (params.data[field] ? params.data[field] : calculation(params.data)),
      cellRenderer: GridService.aggregateCellRenderer,
    } as ColDef;
  }

  checkbox(
    field: string,
    headerName: string,
    edited?: (model: any) => void,
    editable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    const canEdit = (params) => editable && editable(params.data);
    return {
      ...this.text(field, headerName, options),
      cellClass: 'd-flex align-items-center',
      cellRenderer: 'checkboxRenderer',
      enableRowGroup: false,
      editable: (params: EditableCallbackParams) => canEdit(params),
      onCellValueChanged: (params) => (canEdit(params) && edited ? edited(params.data) : undefined),
      filterValueGetter: (params: ValueGetterParams) =>
        params.data[field] ? BooleanStrings.translatedYes : BooleanStrings.translatedNo,
    } as ColDef;
  }

  /**
   * A row-select checkbox hung off a REAL data column (a made-up field renders `true`
   * in the cell once ticked).
   *
   * A read-only role normally gets a plain, uncheckable column, because selecting rows
   * is nearly always the first step of a write action. Pass allowWhenReadOnly for a
   * selection that drives a NON-write action — bulk printing — which read-only roles
   * are entitled to do, exactly as they can print a single document.
   */
  checkboxRowSelect(
    field: string,
    headerName: string,
    showSelectAll?: boolean | ((params: any) => boolean),
    showCheckbox?: (model: any) => boolean,
    options?: GridFieldOptions,
    allowWhenReadOnly = false,
  ): ColDef[] {
    return this.menuService.isReadOnly && !allowWhenReadOnly
      ? [this.checkbox('selected', ''), this.text(field, headerName)]
      : [
          {
            ...this.text(field, headerName, options),
            checkboxSelection: (params: CheckboxSelectionCallbackParams) =>
              showCheckbox ? showCheckbox(params.data) : true,
            headerCheckboxSelection: showSelectAll,
            headerCheckboxSelectionFilteredOnly: !!showSelectAll,
            // Grouped by it, the column (and every tick box) would leave the grid.
            enableRowGroup: false,
          } as ColDef,
        ];
  }

  clickableField(
    field: string,
    headerName: string,
    clicked?: (model: any) => void,
    clickable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    const canEdit = (params) => !params.data?.isAggregate && (!clickable || clickable(params.data));
    return {
      ...this.text(field, headerName, options),
      cellRenderer: GridService.aggregateCellRenderer,
      onCellClicked: (params: CellClickedEvent) => (canEdit(params) && clicked ? clicked(params.data) : undefined),
      cellStyle: (params) =>
        canEdit(params)
          ? { color: 'var(--tms-grid-link)', cursor: 'pointer', 'text-decoration': 'underline' }
          : { cursor: 'default', 'text-decoration': 'none' },
    };
  }

  /**
   * A clickable field holding a NUMBER - `clickableField` with the numeric filter that
   * `number()` adds. Use it for a reference the user clicks through on (a despatch note
   * number, an order number); `clickableNumberFormatField` is for a formatted VALUE, and
   * would print a document number with decimal places.
   */
  clickableNumber(
    field: string,
    headerName: string,
    clicked?: (model: any) => void,
    clickable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    return {
      ...this.clickableField(field, headerName, clicked, clickable, options),
      filter: 'agNumberColumnFilter',
      enableRowGroup: false,
      filterParams: {
        buttons: ['reset'],
      },
    };
  }

  clickableNumberFormatField(
    field: string,
    headerName: string,
    clicked?: (model: any) => void,
    clickable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    const canEdit = (params) => !params.data?.isAggregate && (!clickable || clickable(params.data));
    return {
      ...this.numberFormat(field, headerName, options),
      onCellClicked: (params: CellClickedEvent) => (canEdit(params) && clicked ? clicked(params.data) : undefined),
      cellStyle: (params) =>
        canEdit(params)
          ? { color: 'var(--tms-grid-link)', cursor: 'pointer', 'text-decoration': 'underline' }
          : { cursor: 'default', 'text-decoration': 'none' },
    };
  }

  date(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      comparator: this.dateComparator,
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: this.dateComparator,
        buttons: ['reset'],
      },
      valueGetter: (params: ValueGetterParams) => this.formatService.date(params.data ? params.data[field] : undefined),
      cellRenderer: (params: ICellRendererParams) =>
        GridService.aggregateCellRenderer({
          ...params,
          value: params.data ? this.formatService.date(params.data[field]) : params.value,
        }),
      cellStyle: { overflow: 'visible' },
    };
  }

  dateTime(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.date(field, headerName, options),
      valueGetter: (params: ValueGetterParams) =>
        params.data ? this.formatService.dateTime(params.data[field]) : undefined,
      cellRenderer: (params: ICellRendererParams) =>
        GridService.aggregateCellRenderer({
          ...params,
          value: params.data ? this.formatService.dateTime(params.data[field]) : params.value,
        }),
    };
  }

  editableDate(
    field: string,
    headerName: string,
    edited?: (model: any) => void,
    editable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    const canEdit = (params) => !params.data?.isAggregate && (!editable || editable(params.data));
    if (this.menuService.isReadOnly) {
      return this.date(field, headerName, options);
    }

    return {
      ...this.date(field, headerName, options),
      cellClass: 'd-flex align-items-center',
      cellRenderer: 'dateRenderer',
      editable: (params: EditableCallbackParams) => canEdit(params),
      onCellValueChanged: (params) => (canEdit(params) && edited ? edited(params.data) : undefined),
    };
  }

  editableField(
    field: string,
    headerName: string,
    edited?: (model: any) => void,
    editable?: (model: any) => boolean,
    options?: GridFieldOptions,
    cellStyle?: (params: CellClassParams) => any,
    regex?: RegExp,
  ): ColDef {
    if (this.menuService.isReadOnly) {
      return this.text(field, headerName, options);
    }

    // A group or footer row on a grid the user groups (app-grid rowGrouping) has no data to edit.
    const canEdit = (params) =>
      (!!params.data || !params.context?.rowGrouping) &&
      !params.data?.isAggregate &&
      (!editable || editable(params.data));
    return {
      ...this.text(field, headerName, options),
      cellStyle: (params) => ({
        ...(canEdit(params) ? { color: 'var(--tms-grid-link)', cursor: 'text' } : { cursor: 'default' }),
        ...(cellStyle ? cellStyle(params) : {}),
      }),
      editable: (params: EditableCallbackParams) => canEdit(params),
      onCellValueChanged: (params) => (canEdit(params) && edited ? edited(params.data) : undefined),
      singleClickEdit: true,
      valueSetter: regex
        ? (params) => {
            if (regex.test(params.newValue ?? '')) {
              params.data[field] = params.newValue;
              return true;
            } else {
              return false;
            }
          }
        : undefined,
    };
  }

  editableNumberFormat(
    field: string,
    headerName: string,
    edited?: (model: any) => void,
    editable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    if (this.menuService.isReadOnly) {
      return this.numberFormat(field, headerName, options);
    }

    return {
      ...this.editableField(field, headerName, edited, editable, options),
      filter: 'agNumberColumnFilter',
      enableRowGroup: false,
      cellRenderer: (params: ICellRendererParams) =>
        GridService.aggregateCellRenderer({
          ...params,
          value: this.formatService.number(params.data?.[field] ?? params.value, options?.decimalPlaces),
        }),
    };
  }

  image(field: string, headerName: string, width = 200, height = 150, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      cellRenderer: 'imageRenderer',
      cellRendererParams: {
        width,
        height,
      },
      width,
      autoHeight: true,
      filter: false,
      sortable: false,
      suppressColumnsToolPanel: true,
      enableRowGroup: false,
      ...options,
    };
  }

  number(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      filter: 'agNumberColumnFilter',
      enableRowGroup: false,
      filterParams: {
        buttons: ['reset'],
      },
    };
  }

  currency(currencyField: string, field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      filter: 'agNumberColumnFilter',
      enableRowGroup: false,
      filterParams: {
        buttons: ['reset'],
      },
      cellRenderer: (params: ICellRendererParams) =>
        GridService.aggregateCellRenderer({
          ...params,
          value: this.formatService.currency(
            params.data?.[field] ?? params.value,
            params.data?.[currencyField] ?? '',
            options?.decimalPlaces,
          ),
        }),
      cellStyle: (params: CellClassParams) =>
        options?.colourNegatives && params.value < 0 ? { color: 'var(--tms-grid-negative)' } : {},
      ...{ currencyField },
    };
  }

  numberFormat(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      filter: 'agNumberColumnFilter',
      enableRowGroup: false,
      filterParams: {
        buttons: ['reset'],
      },
      cellRenderer: (params: ICellRendererParams) => {
        // On a grid the user groups (app-grid rowGrouping), a column with no total is blank on
        // the group and footer rows rather than 0.00.
        if (params.context?.rowGrouping && !params.data && params.value == null) {
          return '';
        }
        let value = params.data?.[field] ?? params.value;
        if (options?.negateValue) {
          value = isNaN(+value) ? 0 : +value * -1;
        }
        return GridService.aggregateCellRenderer({
          ...params,
          value:
            options?.zeroOrNegativeMessageField && params.data[options?.zeroOrNegativeMessageField] && value <= 0
              ? (params.data[options?.zeroOrNegativeMessageField] ?? $localize`Out of stock`)
              : this.formatService.number(value, options?.decimalPlaces),
        });
      },
      cellStyle: (params) =>
        options?.colourNegatives && params.value < 0 ? { color: 'var(--tms-grid-negative)' } : {},
    };
  }

  rowNumber(options?: GridFieldOptions): ColDef {
    return {
      valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
      maxWidth: 40,
      ...options,
    } as ColDef;
  }

  // `selectOptions` is one list for the column, or a function of the row's data when each row
  // needs its own list (see SelectRendererComponent). `options.show` (a row predicate) hides
  // the select on rows it returns false for, as the other renderers do.
  select(
    field: string,
    headerName: string,
    selectOptions?:
      | Observable<IdDescription[]>
      | IdDescription[]
      | ((rowData: any) => Observable<IdDescription[]> | IdDescription[]),
    options?: GridFieldOptions & { show?: (rowData: any) => boolean },
  ): ColDef {
    if (this.menuService.isReadOnly) {
      return this.text(field, headerName, options);
    }

    return {
      ...this.text(field, headerName, options),
      cellRenderer: 'selectRenderer',
      cellRendererParams: {
        options:
          typeof selectOptions === 'function'
            ? (rowData: any) => this.utilityService.mapSelectOptions(selectOptions(rowData))
            : this.utilityService.mapSelectOptions(selectOptions),
        show: options?.show,
      },
    };
  }

  text(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    const valueGetter = options?.translateYes ? GridService.translateYes(field) : options?.valueGetter;
    return {
      field,
      headerName: this.languageService.translate(headerName, options?.abbreviatedHeaderName),
      width: 200,
      filterParams: {
        buttons: ['reset'],
      },
      cellRenderer: GridService.aggregateCellRenderer,
      ...options,
      ...(options?.translateYes ? { valueGetter } : {}),
      ...(options?.blankIfAggregate ? { valueGetter: GridService.blankIfAggregate(field, valueGetter) } : {}),
    } as ColDef;
  }

  textEllipsis(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      cellStyle: { overflow: 'hidden', 'text-overflow': 'ellipsis', 'white-space': 'nowrap' },
    } as ColDef;
  }

  setCurrencyTotalColumn(
    gridApi: GridApi,
    columnDefs: ColDef[],
    symbol: string,
    options?: {
      field?: string;
      appendIfMissing?: boolean;
      extra?: Partial<ColDef>;
    },
  ): ColDef[] {
    const field = options?.field ?? 'sterlingTotal';

    const built = {
      ...this.numberFormat(field, $localize`Total (${symbol})`, { aggFunc: AggFunc.sum }),
      ...(options?.extra ?? {}),
    } as ColDef;

    const next = columnDefs.slice();
    const idx = next.findIndex((c) => c.field === field);

    if (idx !== -1) next[idx] = built;
    else if (options?.appendIfMissing) next.push(built);

    gridApi.setColumnDefs(next);
    return next;
  }

  private dateComparator = (filterLocalDateAtMidnight: string | Date, cellValue: string | Date) => {
    const cellDate = this.toDate(cellValue);
    const filterDate = this.toDate(filterLocalDateAtMidnight);

    if (!cellDate || !filterDate) {
      return -1;
    }

    if (filterDate.getTime() === cellDate.getTime()) {
      return 0;
    }

    if (cellDate < filterDate) {
      return -1;
    }

    if (cellDate > filterDate) {
      return 1;
    }

    return 0;
  };

  private toDate(date: string | Date): Date {
    if (date === undefined) {
      return undefined;
    }

    if (date instanceof Date) {
      return date;
    }

    return this.dateFormatService.dateFromString(date);
  }

  // A helper that sets its own valueGetter after calling text() (date, dateTime, calculation)
  // replaces this one, so the option applies to text() and the helpers that keep its getter.
  private static blankIfAggregate(field: string, valueGetter?: ColDef['valueGetter']) {
    return (params: ValueGetterParams) => {
      if (params.data?.isAggregate) {
        return '';
      }

      return typeof valueGetter === 'function' ? valueGetter(params) : params.data?.[field];
    };
  }

  private static translateYes(field: string) {
    return (params: ValueGetterParams) =>
      params.data?.[field] === BooleanStrings.yes ? BooleanStrings.translatedYes : '';
  }

  private static aggregateCellRenderer(params: ICellRendererParams) {
    if (!params.value && params.value !== 0) {
      return '';
    }
    if (params.data?.isAggregate) {
      return params.data[params.colDef.field] ? `<strong>${params.value}</strong>` : '';
    }

    const aggFunc = params.colDef.aggFunc;
    if (params.node.group) {
      switch (aggFunc) {
        case AggFunc.custom:
        case AggFunc.avg:
        case AggFunc.sum:
        case AggFunc.sumInteger:
        case AggFunc.count:
          return `<strong>${params.value}</strong>`;
        default:
          return params.value;
      }
    }

    switch (aggFunc) {
      case AggFunc.first:
      case AggFunc.last:
      case AggFunc.min:
      case AggFunc.max:
        return '';
      default:
        return params.value;
    }
  }
}
